import { Smile } from 'lucide-react';
import {
    type ComponentProps,
    lazy,
    Suspense,
    useEffect,
    useRef,
    useState,
} from 'react';

import { Button } from '@apps/web/components/ui/button';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@apps/web/components/ui/popover';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
} from '@apps/web/components/ui/sheet';
import { Spinner } from '@apps/web/components/ui/spinner';
import { useIsMobile } from '@apps/web/hooks/use_mobile';
import { useTheme } from '@apps/web/lib/theme_store';
import { cn } from '@apps/web/lib/utils';

// Emoji Mart ships its own emoji index and rendering, so it is only fetched
// once someone actually opens the picker.
const EmojiPicker = lazy(async () => {
    const [{ default: Picker }, { default: data }] = await Promise.all([
        import('@emoji-mart/react'),
        import('@emoji-mart/data'),
    ]);

    return {
        default: (props: Omit<ComponentProps<typeof Picker>, 'data'>) => (
            <Picker {...props} data={data} />
        ),
    };
});

type EmojiSelection = {
    readonly native?: string;
};

export type MessageEmojiPickerProps = {
    readonly disabled: boolean;
    readonly onBeforeOpen?: () => void;
    readonly onSelect: (emoji: string) => void;
};

function removeUnsupportedEmojiMartAria(root: ShadowRoot): void {
    // Emoji Mart 5.6.0 puts listbox-only position/selection attributes on
    // native buttons. The labels and button behavior are already sufficient,
    // so strip those invalid attributes until an upstream release supplies
    // list roles.
    root.querySelectorAll<HTMLButtonElement>(
        'button[aria-posinset], button[aria-selected], button[aria-setsize]',
    ).forEach((button) => {
        button.removeAttribute('aria-posinset');
        button.removeAttribute('aria-selected');
        button.removeAttribute('aria-setsize');
    });
}

export function MessageEmojiPicker({
    disabled,
    onBeforeOpen,
    onSelect,
}: MessageEmojiPickerProps) {
    const [open, setOpen] = useState(false);
    const theme = useTheme();
    const isMobile = useIsMobile();
    const pickerContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) {
            return;
        }
        let activeShadowRoot: ShadowRoot | null = null;
        let hostInterval: number | null = null;
        let shadowObserver: MutationObserver | null = null;
        const connectShadowRoot = () => {
            const shadowRoot =
                pickerContainerRef.current?.querySelector(
                    'em-emoji-picker',
                )?.shadowRoot;
            if (!shadowRoot || shadowRoot === activeShadowRoot) {
                return;
            }
            shadowObserver?.disconnect();
            activeShadowRoot = shadowRoot;
            if (hostInterval !== null) {
                window.clearInterval(hostInterval);
                hostInterval = null;
            }
            removeUnsupportedEmojiMartAria(shadowRoot);
            shadowObserver = new MutationObserver(() => {
                removeUnsupportedEmojiMartAria(shadowRoot);
            });
            shadowObserver.observe(shadowRoot, {
                attributeFilter: [
                    'aria-posinset',
                    'aria-selected',
                    'aria-setsize',
                ],
                attributes: true,
                childList: true,
                subtree: true,
            });
        };
        connectShadowRoot();
        if (!activeShadowRoot) {
            // The React wrapper can append the custom element before Emoji Mart
            // attaches its shadow root, and that attachment emits no light-DOM
            // mutation to observe. Stop this bounded discovery poll as soon as
            // the root becomes available.
            hostInterval = window.setInterval(connectShadowRoot, 50);
        }
        return () => {
            if (hostInterval !== null) {
                window.clearInterval(hostInterval);
            }
            shadowObserver?.disconnect();
        };
    }, [open]);

    const handleOpenChange = (nextOpen: boolean) => {
        if (nextOpen) {
            onBeforeOpen?.();
        }
        setOpen(nextOpen);
    };
    const handleEmojiSelect = (emoji: EmojiSelection) => {
        if (emoji.native) {
            onSelect(emoji.native);
        }
        setOpen(false);
    };
    const trigger = (
        <Button
            aria-label="Add emoji"
            className="size-8"
            data-testid="emoji-picker-trigger"
            disabled={disabled}
            size="icon"
            type="button"
            variant="ghost"
        >
            <Smile className="size-4" />
        </Button>
    );
    const picker = (
        <div
            className={cn(
                'flex min-h-0 justify-center',
                isMobile &&
                    'w-full flex-1 [&>div]:h-full [&>div]:w-full [&_em-emoji-picker]:h-full [&_em-emoji-picker]:w-full [&_em-emoji-picker]:[--border-radius:0px] [&_em-emoji-picker]:[--shadow:none]',
            )}
            ref={pickerContainerRef}
        >
            <Suspense
                fallback={
                    <div className="bg-popover flex size-16 items-center justify-center rounded-md border">
                        <Spinner className="size-4" />
                    </div>
                }
            >
                <EmojiPicker
                    dynamicWidth={isMobile}
                    emojiButtonSize={36}
                    emojiSize={24}
                    onEmojiSelect={handleEmojiSelect}
                    perLine={isMobile ? undefined : 9}
                    previewPosition="none"
                    set="native"
                    skinTonePosition="search"
                    theme={theme === 'system' ? 'auto' : theme}
                />
            </Suspense>
        </div>
    );

    if (isMobile) {
        return (
            <Sheet onOpenChange={handleOpenChange} open={open}>
                <SheetTrigger asChild={true}>{trigger}</SheetTrigger>
                <SheetContent
                    className="h-[min(80dvh,36rem)] gap-0 overflow-hidden rounded-t-2xl border-none p-0 pt-10"
                    data-testid="emoji-picker-mobile"
                    onCloseAutoFocus={(event) => event.preventDefault()}
                    onOpenAutoFocus={(event) => event.preventDefault()}
                    side="bottom"
                >
                    <SheetHeader
                        className="sr-only"
                        data-testid="emoji-picker-mobile-accessible-header"
                    >
                        <SheetTitle>Add emoji</SheetTitle>
                        <SheetDescription>
                            Search or browse native emoji.
                        </SheetDescription>
                    </SheetHeader>
                    {picker}
                </SheetContent>
            </Sheet>
        );
    }

    return (
        <Popover onOpenChange={handleOpenChange} open={open}>
            <PopoverTrigger asChild={true}>{trigger}</PopoverTrigger>
            <PopoverContent
                align="start"
                className="w-auto border-none p-0 shadow-none"
                data-testid="emoji-picker-desktop"
                onCloseAutoFocus={(event) => event.preventDefault()}
                side="top"
            >
                {picker}
            </PopoverContent>
        </Popover>
    );
}
